"use client";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  X,
  Building2,
  DollarSign,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { FiMail, FiCheck } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import {
  parsePhoneNumberFromString,
  getCountryCallingCode,
} from "libphonenumber-js";

import { LocationSelector } from "../../components/LocationSelector";
import { CustomAlertModal } from "../../components/CustomAlertModal";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { saveToken } from "../../utils/localStorage";
import { getUserCurrencyThunk } from "../../redux/slices/profileSlice/asyncthunk";
import { PrimaryButton } from "../../components/buttons/primaryButton/PrimaryButton";
import { PhoneField } from "../../components/PhoneField/PhoneField";
const API_URL = REST_API_URL;
const USER_INFO_URL = `${API_URL}/auth/userinfo/`;

const getNationalNumber = (fullPhoneNumber) => {
  if (!fullPhoneNumber) return "";
  const parsed = parsePhoneNumberFromString(fullPhoneNumber);
  if (parsed) {
    return parsed.nationalNumber;
  }
  return fullPhoneNumber;
};

const getFullPhoneNumber = (nationalNumber, countryCode) => {
  if (!nationalNumber) return "";
  return `${countryCode}${nationalNumber}`;
};

export default function EditBusinessModal({ onClose, userData }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [userType, setUserType] = useState(
    userData?.user?.status || "individual",
  );
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState("AM");
  const [countryCode, setCountryCode] = useState("+374");

  const [currency, setCurrency] = useState("USD");
  const [currencies, setCurrencies] = useState([]);
  const [openCurrency, setOpenCurrency] = useState(false);
  const [loadingCurrency, setLoadingCurrency] = useState(true);

  const [position, setPosition] = useState(null);
  const [addressDetails, setAddressDetails] = useState({
    country: "",
    city: "",
    address: "",
    state: "",
    postcode: "",
    fullAddress: "",
  });

  const [selectedWorkPlaces, setSelectedWorkPlaces] = useState([]);
  const [allWorkplaces, setAllWorkplaces] = useState([]);

  const [initialTimeZone, setInitialTimeZone] = useState("");
  const [timeZone, setTimeZone] = useState("");
  const [modalError, setModalError] = useState("");
  const [alertVisible, setAlertVisible] = useState(false);

  const [loading, setLoading] = useState(false);

  const isInitialLocation = useRef(true);
  const previousPosition = useRef(null);
  const currencyRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (currencyRef.current && !currencyRef.current.contains(event.target)) {
        setOpenCurrency(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch & populate user data from backend / prop
  useEffect(() => {
    (async () => {
      try {
        let data = userData;
        if (!data || !data.user) {
          const res = await fetch(USER_INFO_URL, { headers: buildHeaders() });
          if (res.ok) {
            data = await res.json();
          }
        }

        if (!data) return;

        setUserType(data.user?.status || "individual");

        setFullName(
          `${data.user?.first_name || ""} ${data.user?.last_name || ""}`.trim() ||
            data.user?.full_name ||
            "",
        );
        setEmail(data.user?.email || "");

        const nationalNumber = getNationalNumber(data.phone_number || "");
        setPhone(nationalNumber);

        if (data.phone_number) {
          const parsed = parsePhoneNumberFromString(data.phone_number);
          if (parsed && parsed.country) {
            setSelectedCountry(parsed.country);
            setCountryCode("+" + getCountryCallingCode(parsed.country));
          }
        }

        setCompanyName(data.user?.company_name || "");

        setAddressDetails({
          country: "",
          city: data.city || "",
          address: data.address || "",
          state: "",
          postcode: "",
          fullAddress: data.address || "",
        });

        let parsedPosition = null;
        if (data.address_url) {
          const match = data.address_url.match(/(?:query|q)=([^&]+)/);
          if (match) {
            const coords = match[1].split(",");
            if (coords.length >= 2) {
              const lat = parseFloat(coords[0]);
              const lng = parseFloat(coords[1]);
              if (!isNaN(lat) && !isNaN(lng)) {
                parsedPosition = { lat, lng };
              }
            }
          }
          if (!parsedPosition) {
            const pathMatch = data.address_url.match(
              /@(-?\d+\.\d+),(-?\d+\.\d+)/,
            );
            if (pathMatch) {
              const lat = parseFloat(pathMatch[1]);
              const lng = parseFloat(pathMatch[2]);
              if (!isNaN(lat) && !isNaN(lng)) {
                parsedPosition = { lat, lng };
              }
            }
          }
        }

        if (!parsedPosition && data.lat && data.lng) {
          const lat = parseFloat(data.lat);
          const lng = parseFloat(data.lng);
          if (!isNaN(lat) && !isNaN(lng)) {
            parsedPosition = { lat, lng };
          }
        }

        const finalPos = parsedPosition || { lat: 40.7128, lng: -74.006 };
        setPosition(finalPos);
        previousPosition.current = finalPos;
        isInitialLocation.current = true;

        if (data.currency) setCurrency(data.currency);

        if (Array.isArray(data.work_place)) {
          setSelectedWorkPlaces(data.work_place.map((p) => p.id));
        }

        setInitialTimeZone(data.time_zone || "");
        setTimeZone(data.time_zone || "");
        if (data.time_zone) {
          saveToken("time_zone", data.time_zone);
        }
      } catch (err) {
        console.error("Error loading user info in modal:", err);
      }
    })();
  }, [userData]);

  // Fetch currencies
  useEffect(() => {
    (async () => {
      setLoadingCurrency(true);
      try {
        const res = await fetch(`${API_URL}/service/currencies/`, {
          headers: buildHeaders(),
        });
        const data = await res.json();
        if (Array.isArray(data)) {
          setCurrencies(
            data.map((item) => ({
              code: item.short_name,
              name: item.short_name,
              flag: item.icon || "",
            })),
          );
        }
      } catch (err) {
        console.warn("Currency fetch failed:", err);
      } finally {
        setLoadingCurrency(false);
      }
    })();
  }, []);

  // Fetch workplaces for individual
  useEffect(() => {
    if (userType === "individual") {
      (async () => {
        try {
          const res = await fetch(`${API_URL}/auth/workplaces/`, {
            headers: buildHeaders(),
          });
          if (res.ok) {
            const data = await res.json();
            setAllWorkplaces(data);
          }
        } catch (err) {
          console.error("Workplaces fetch error:", err);
        }
      })();
    }
  }, [userType]);

  // Check timezone on location change (via Open-Meteo) only when user changes address
  useEffect(() => {
    if (
      !position ||
      typeof position.lat !== "number" ||
      typeof position.lng !== "number"
    ) {
      return;
    }

    if (isInitialLocation.current) {
      isInitialLocation.current = false;
      previousPosition.current = position;
      return;
    }

    if (
      previousPosition.current &&
      previousPosition.current.lat === position.lat &&
      previousPosition.current.lng === position.lng
    ) {
      return;
    }

    previousPosition.current = position;

    const checkTimezone = async () => {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${position.lat}&longitude=${position.lng}&timezone=auto`,
        );
        if (res.ok) {
          const data = await res.json();
          const newTimezone = data?.timezone;
          const currentTimezone = initialTimeZone || timeZone;

          if (
            newTimezone &&
            currentTimezone &&
            newTimezone !== currentTimezone
          ) {
            setTimeZone(newTimezone);
            setModalError("");
            setAlertVisible(true);
          }
        }
      } catch (err) {
        console.error("Error checking timezone:", err);
      }
    };

    checkTimezone();
  }, [position, initialTimeZone, timeZone]);

  const save = async (overrideTimeZone) => {
    setLoading(true);
    if (!position) {
      toast.error(t("home.errorLocationRequired"));
      setLoading(false);
      return;
    }

    if (userType === "individual" && selectedWorkPlaces.length === 0) {
      toast.error(t("settings.selectAtLeastOneWorkplace"));
      setLoading(false);
      return;
    }

    if (phone && !isPhoneValid) {
      toast.error(t("invalidPhoneNumber"));
      setLoading(false);
      return;
    }

    const fullPhoneNumber = getFullPhoneNumber(phone, countryCode);

    const targetTimeZone =
      typeof overrideTimeZone === "string"
        ? overrideTimeZone
        : timeZone || initialTimeZone;

    const body = {
      full_name: fullName.trim(),
      company_name: userType === "salon" ? companyName.trim() : "",
      phone_number: fullPhoneNumber,
      address_url: `https://www.google.com/maps/search/?api=1&query=${position.lat},${position.lng}`,
      currency: currency || "USD",
      work_place_ids: userType === "individual" ? selectedWorkPlaces : [],
      new_time_zone: targetTimeZone,
    };

    try {
      const res = await fetch(USER_INFO_URL, {
        method: "PUT",
        headers: buildHeaders(),
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        let firstError = "";
        if (data && typeof data === "object") {
          const keys = Object.keys(data);
          if (keys.length > 0) {
            const val = data[keys[0]];
            firstError = Array.isArray(val)
              ? val.join(", ")
              : typeof val === "string"
                ? val
                : JSON.stringify(val);
          }
        }
        const errorText =
          firstError ||
          data?.detail ||
          data?.message ||
          t("settings.somethingWentWrong");
        setModalError(errorText);
        setAlertVisible(true);
        return;
      }

      if (targetTimeZone) {
        saveToken("time_zone", targetTimeZone);
      }

      dispatch(getUserCurrencyThunk());
      window.dispatchEvent(new Event("userUpdated"));
      toast.success(t("settings.profileUpdatedSuccessfully"));
      onClose(true);
    } catch (err) {
      console.error(err);
      setModalError(err?.message || t("settings.somethingWentWrong"));
      setAlertVisible(true);
    } finally {
      setLoading(false);
    }
  };

  const selectedCurrencyObj = currencies.find((c) => c.code === currency);

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 backdrop-blur-sm p-4"
        style={{ marginTop: "0px" }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="bg-white rounded-[24px] w-full max-w-[780px] shadow-2xl flex flex-col gap-6 max-h-[90vh] overflow-y-auto no-scrollbar p-6 sm:p-8"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 pb-4">
            <h2 className="text-[20px] font-bold text-[#302B2B] flex items-center gap-2">
              <Building2 className="w-6 h-6 text-gray-700" />
              {t("home.editAdditionalInfoTitle")}
            </h2>
            <button
              onClick={() => onClose(false)}
              className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
          </div>

          {/* Form Content */}
          <div className="flex flex-col gap-6">
            {/* Full Name & Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {t("settings.fullName")}
                </label>
                <input
                  type="text"
                  className="h-12 w-full px-4 rounded-xl border border-gray-200 focus:border-[#020637] focus:ring-2 focus:ring-[#020637]/20 transition-all bg-white text-gray-800 text-sm shadow-sm"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Doe"
                />
              </div>
              {/* Phone Field */}
              <PhoneField
                phone={phone}
                country={selectedCountry}
                label={t("phoneNumber")}
                required={false}
                onPhoneChange={(val, valid) => {
                  setPhone(val);
                  setIsPhoneValid(valid);
                }}
                onCountryChange={(c, code) => {
                  setSelectedCountry(c);
                  if (code) {
                    setCountryCode(code);
                  } else {
                    setCountryCode("+" + getCountryCallingCode(c));
                  }
                }}
              />
            </div>

            {/*   Company Name / Currency */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
             {userType !== "worker"  && <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {t("settings.email")}
                </label>
                <div className="relative">
                  <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    className="h-12 w-full pl-11 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 text-sm cursor-not-allowed"
                    value={email}
                    disabled
                  />
                </div>
              </div>}

              {/* Company Name (for salon) or Currency (for individual) */}
              {userType === "salon" ? (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {t("settings.companyName")}
                  </label>
                  <input
                    type="text"
                    className="h-12 w-full px-4 rounded-xl border border-gray-200 focus:border-[#020637] focus:ring-2 focus:ring-[#020637]/20 transition-all bg-white text-gray-800 text-sm shadow-sm"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder={t("settings.companyName")}
                  />
                </div>
              ) : (
                /* Currency dropdown for individual */
                userType !== "worker" && (
                  <div className="flex flex-col gap-2" ref={currencyRef}>
                    <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      {t("settings.currency")}
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setOpenCurrency(!openCurrency)}
                        className="h-12 w-full px-4 rounded-xl border border-gray-200 bg-white hover:border-gray-300 focus:border-[#020637] focus:ring-2 focus:ring-[#020637]/20 transition-all flex items-center justify-between text-sm shadow-sm"
                      >
                        <div className="flex items-center gap-2">
                          {selectedCurrencyObj?.flag ? (
                            <img
                              src={selectedCurrencyObj.flag}
                              alt=""
                              className="w-5 h-5 rounded-full object-cover"
                            />
                          ) : (
                            <DollarSign className="w-4 h-4 text-gray-500" />
                          )}
                          <span className="font-medium text-gray-800">
                            {selectedCurrencyObj?.name ||
                              currency ||
                              t("auth.selectCurrency")}
                          </span>
                        </div>
                        <ChevronDown
                          className={`w-4 h-4 text-gray-500 transition-transform ${
                            openCurrency ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      <AnimatePresence>
                        {openCurrency && (
                          <motion.div
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            className="absolute z-50 top-full left-0 w-full bg-white border border-gray-100 rounded-xl shadow-2xl mt-1 max-h-56 overflow-y-auto"
                          >
                            {currencies.map((curr) => (
                              <div
                                key={curr.code}
                                onClick={() => {
                                  setCurrency(curr.code);
                                  setOpenCurrency(false);
                                }}
                                className={`px-4 py-2.5 cursor-pointer flex items-center gap-2.5 hover:bg-[#020637]/8 transition text-sm ${
                                  currency === curr.code
                                    ? "bg-[#020637]/5 font-semibold text-[#020637]"
                                    : "text-gray-700"
                                }`}
                              >
                                {curr.flag && (
                                  <img
                                    src={curr.flag}
                                    alt=""
                                    className="w-4 h-4 rounded-full object-cover"
                                  />
                                )}
                                <span>{curr.name}</span>
                              </div>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Currency for salon */}
            {userType === "salon" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex flex-col gap-2" ref={currencyRef}>
                  <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {t("settings.currency")}
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setOpenCurrency(!openCurrency)}
                      className="h-12 w-full px-4 rounded-xl border border-gray-200 bg-white hover:border-gray-300 focus:border-[#020637] focus:ring-2 focus:ring-[#020637]/20 transition-all flex items-center justify-between text-sm shadow-sm"
                    >
                      <div className="flex items-center gap-2">
                        {selectedCurrencyObj?.flag ? (
                          <img
                            src={selectedCurrencyObj.flag}
                            alt=""
                            className="w-5 h-5 rounded-full object-cover"
                          />
                        ) : (
                          <DollarSign className="w-4 h-4 text-gray-500" />
                        )}
                        <span className="font-medium text-gray-800">
                          {selectedCurrencyObj?.name ||
                            currency ||
                            t("auth.selectCurrency")}
                        </span>
                      </div>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-500 transition-transform ${
                          openCurrency ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    <AnimatePresence>
                      {openCurrency && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          className="absolute z-50 top-full left-0 w-full bg-white border border-gray-100 rounded-xl shadow-2xl mt-1 max-h-56 overflow-y-auto"
                        >
                          {currencies.map((curr) => (
                            <div
                              key={curr.code}
                              onClick={() => {
                                setCurrency(curr.code);
                                setOpenCurrency(false);
                              }}
                              className={`px-4 py-2.5 cursor-pointer flex items-center gap-2.5 hover:bg-[#020637]/8 transition text-sm ${
                                currency === curr.code
                                  ? "bg-[#020637]/5 font-semibold text-[#020637]"
                                  : "text-gray-700"
                              }`}
                            >
                              {curr.flag && (
                                <img
                                  src={curr.flag}
                                  alt=""
                                  className="w-4 h-4 rounded-full object-cover"
                                />
                              )}
                              <span>{curr.name}</span>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            )}

            {/* Workplaces (if Individual) */}
            {userType === "individual" && allWorkplaces.length > 0 && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {t("settings.whereDoYouWork")}
                </label>
                <div className="flex flex-wrap gap-3">
                  {allWorkplaces.map((item) => {
                    const isSelected = selectedWorkPlaces.includes(item.id);
                    return (
                      <div
                        key={item.id}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedWorkPlaces(
                              selectedWorkPlaces.filter((id) => id !== item.id),
                            );
                          } else {
                            setSelectedWorkPlaces([
                              ...selectedWorkPlaces,
                              item.id,
                            ]);
                          }
                        }}
                        className={`flex flex-1 min-w-[140px] items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                          isSelected
                            ? "border-[#020637] bg-[#020637]/5"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                      >
                        <div
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors shrink-0 ${
                            isSelected
                              ? "border-[#020637] bg-[#020637] text-white"
                              : "border-gray-300 bg-white"
                          }`}
                        >
                          {isSelected && (
                            <FiCheck className="w-3.5 h-3.5 stroke-[3] text-white" />
                          )}
                        </div>
                        <span
                          className={`text-sm font-medium ${
                            isSelected ? "text-[#020637]" : "text-gray-700"
                          }`}
                        >
                          {item.place_title}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Location Selector */}
            {userType !== "worker" && (
              <div className="flex flex-col gap-2">
                <div className="location-selector-modal-wrapper rounded-2xl overflow-hidden border border-gray-200 p-1">
                  <style>{`.location-selector-modal-wrapper > div { width: 100% !important; }`}</style>
                  <LocationSelector
                    position={position}
                    setPosition={setPosition}
                    setAddressDetails={setAddressDetails}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <PrimaryButton
              onClick={() => onClose(false)}
              variant="ghost"
              label={t("home.cancel")}
              className="px-6"
            />
            <PrimaryButton
              disabled={loading}
              onClick={() => save()}
              leftIcon={
                loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : undefined
              }
              label={loading ? t("auth.saving") : t("home.save")}
              className="px-8"
            />
          </div>
        </motion.div>
      </motion.div>

      {/* CustomAlertModal for timezone changes or server errors */}
      <CustomAlertModal
        visible={alertVisible}
        onClose={() => setAlertVisible(false)}
        text={modalError || t("TBio.timezoneMismatch")}
        onCancelPress={() => {
          setTimeZone(initialTimeZone);
        }}
        onDonePress={() => {
          setModalError("");
          save();
        }}
        doneText={modalError ? "OK" : t("TBio.updateTimeZone")}
        cancelText={t("TBio.keepPreviousTimeZone")}
        isError={!!modalError}
        onSingleButton={!!modalError}
      />
    </>
  );
}
